import type { ComputedRef, InjectionKey, Ref } from 'vue';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import type { SlugParts } from '@pkg/sanity-utils/urlToSlugParts';
import type { Origin } from '@pkg/types/surpriseBag';

export interface LocationAutocompleteApi {
  suggestions: Ref<Origin[]>;
  loadingSuggestions: Ref<boolean>;
  fetchSuggestions: (searchString: string) => void | Promise<void>;
  initialSearchValue: Ref<string | undefined> | ComputedRef<string | undefined>;
  handleLocationSelected: (option: Origin, searchContext?: 'header') => void;
  buildFindUrl: (option: Origin) => string;
}

/**
 * Use Symbol.for so provide/inject still match when Vite loads
 * `@pkg/site-chrome` and `@pkg/site-chrome/components/...` as separate module graphs
 * (plain Symbol() would be a different reference in each copy → inject returns null).
 */
export const locationAutocompleteKey = Symbol.for(
  'tgtg.site-chrome.locationAutocomplete',
) as InjectionKey<LocationAutocompleteApi | null>;

export interface LanguagePickerApi {
  /**
   * Slug (with parents, no locale prefix) of the page matching `slugParts` in `locale`,
   * or null when that locale has no such page. Page lookup is app-specific
   * (audience filter + fetch endpoint), so each app provides it.
   */
  resolveLocaleSlug: (locale: LOCALE_CODE, slugParts: SlugParts) => Promise<string | null>;
}

/** Without a provider the picker navigates to the target locale's home page. */
export const languagePickerKey = Symbol.for(
  'tgtg.site-chrome.languagePicker',
) as InjectionKey<LanguagePickerApi | null>;
